import { computed, isRef, ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import i18n from '@/plugins/translationsPlugin'
import type { LanguageSettingsState } from '@/types/LanguageSettingsState'
import type { PrintSettingsState } from '@/types/PrintSettingsState'
import type { LastTicketsSettingsState } from '@/types/LastTicketsSettingsState'
import {
  useDeviceManagementPrintStore,
  useDeviceManagementStore,
  type DMLanguage,
} from '@nsftx/seven-client-retail-vue-dm'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'

const LS_KEY = 'pos_settings_state'
const DEFAULT_LATEST_BETSLIPS_SETTINGS = {
  bestlipsFromGame: 'show_all',
  numberOfBetslips: 5,
} satisfies LastTicketsSettingsState

export const useSettingsStore = defineStore('settingsStore', () => {
  const { t } = i18n.global

  const { showNotification } = useNotificationsStore()
  const deviceManagementPrintStore = useDeviceManagementPrintStore()
  const { selectedPrinter, detectedPrinters, supportedPrinters } = storeToRefs(
    deviceManagementPrintStore,
  )
  const { appData } = storeToRefs(useDeviceManagementStore())

  const availableLanguages = ref<DMLanguage[]>([])
  const languageSettings = ref<LanguageSettingsState>({
    interfaceLanguage: '',
    printingLanguage: '',
  })
  const printSettings = ref<PrintSettingsState>({
    posPrinter: selectedPrinter.value?.id,
    posPrinterConfiguration: undefined,
  })
  const lastTicketsSettings = ref<LastTicketsSettingsState>(DEFAULT_LATEST_BETSLIPS_SETTINGS)

  const isFormChanged = ref(false)
  const isUnsavedSettingsModalOpen = ref(false)
  const pendingRoute = ref<string | null>(null)

  const interfaceLanguage = computed(() => languageSettings.value.interfaceLanguage)

  const saveSettingsToStorage = () => {
    const posPrinterToSave = detectedPrinters.value.find(
      (printer) => printer.printer.id === printSettings.value.posPrinter,
    )
    const posPrinterConfigurationToSave = supportedPrinters.value.find(
      (printer) => printer.Id === printSettings.value.posPrinterConfiguration,
    )

    if (posPrinterToSave) {
      deviceManagementPrintStore.setPrinter(posPrinterToSave?.printer)
    }

    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        languages: languageSettings.value,
        print: {
          posPrinterConfiguration: posPrinterConfigurationToSave,
        },
        lastTickets: lastTicketsSettings.value,
      }),
    )

    showNotification({ variant: 'success', text: [t('settings_save_success')] })
    changeLanguageLocale(languageSettings.value.interfaceLanguage)
    isFormChanged.value = false
  }

  const getStoredSettings = () => {
    const storedSettings = JSON.parse(localStorage.getItem(LS_KEY) || '{}')
    languageSettings.value = storedSettings.languages || {}
    printSettings.value.posPrinter = selectedPrinter.value?.id
    printSettings.value.posPrinterConfiguration =
      storedSettings.print?.posPrinterConfiguration?.Id ||
      (selectedPrinter.value &&
        deviceManagementPrintStore.getAlternativePrinterConfigId(selectedPrinter.value))
    lastTicketsSettings.value = {
      ...DEFAULT_LATEST_BETSLIPS_SETTINGS,
      ...(storedSettings.lastTickets ?? {}),
    }
  }

  const resetSettingsForm = () => {
    getStoredSettings()
    isFormChanged.value = false
  }

  const touchForm = () => {
    isFormChanged.value = true
  }

  const isInterfaceLanguageSupported = (languageCode: string) => {
    return availableLanguages.value.some((lang) => lang.language_code_2 === languageCode)
  }

  // TODO: When intergrated with frazier, fetch locale json file from API and apply it
  const changeLanguageLocale = (languageCode: string) => {
    if (!isInterfaceLanguageSupported(languageCode)) {
      return
    }

    // for type checking
    if (isRef(i18n.global.locale)) {
      i18n.global.locale.value = languageCode
    }
  }

  const loadLanguages = () => {
    availableLanguages.value = appData.value?.appConfig.data.supportedLanguages || []
    changeLanguageLocale(languageSettings.value.interfaceLanguage)
  }

  const resolveBootstrapLang = (defaultLanguage: DMLanguage): string => {
    return languageSettings.value.interfaceLanguage || defaultLanguage.language_code_2
  }

  return {
    languageSettings,
    printSettings,
    lastTicketsSettings,
    availableLanguages,
    isFormChanged,
    isUnsavedSettingsModalOpen,
    pendingRoute,
    interfaceLanguage,
    saveSettingsToStorage,
    getStoredSettings,
    loadLanguages,
    resolveBootstrapLang,
    resetSettingsForm,
    touchForm,
  }
})
